import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useClickOutside } from '../../hooks/useClickOutside'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { TmbControlsRow, TmbStratificationPill, TmbQuestionPill, TmbPillSelect } from '../../components/TMBControls'
import {
    normalizeKey,
    safeNumber,
    findColumnIndex,
    findColumnIndexByRegex,
    parseMonthToValue,
    resolveMonthIdx,
    formatDateLabel,
    COLUMN_CANDIDATES
} from '../../utils/dataUtils'
import {
    MonthInfo,
    QuestionInfo,
    normalizeHuddleGroup,
    extractQuestionsFromRows,
    generateMonthList,
    buildQuestionLabelMap,
    initializeSliderValues,
    getSliderIndicesFromValues,
    TMB_SLIDER_KEY,
    extractActualMonthValues,
    checkHasDataForRange
} from '../../utils/tmbUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { applyLaunchDateFilterToZohoData } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './AgreementEthnicity.css'

interface HuddleTotals {
    [key: string]: { num: number; denom: number; hasNum: boolean; hasDenom: boolean }
}

const PAGE_HEADING_LINE_1 = 'Percent of Patients who "Strongly Agree"'
const PAGE_HEADING_LINE_2 = 'by Huddle Status & Race/Ethnicity'

const BASE_MAX_RACE_SELECTIONS = 6
const OTHER_RACE_ETHNICITY_LABEL = "Other Race/Ethnicity"

const ALL_OTHERS_API_VALUE = "All Other Groups"
const ALL_OTHERS_LABEL = "BIPOC"

const AgreementEthnicity: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()
    const [rawData, setRawData] = useState<any>(null)
    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const [selectedQuestion, setSelectedQuestion] = usePersistedState<string>('agreement_ethnicity_question', '')
    const [stratification, setStratification] = usePersistedState<string>('agreement_ethnicity_stratification', 'ExcludeNotSure')

    const DEFAULT_RACE_SELECTIONS = ['Hispanic', 'Black/African American', 'NH White', 'Asian', 'Race Not Documented', OTHER_RACE_ETHNICITY_LABEL]
    const [selectedRaceGroups, setSelectedRaceGroups] = usePersistedState<string[]>('agreement_ethnicity_race_groups', DEFAULT_RACE_SELECTIONS)
    const [availableRaceGroups, setAvailableRaceGroups] = useState<string[]>([])

    const [raceDropdownOpen, setRaceDropdownOpen] = useState(false)
    const [questions, setQuestions] = useState<QuestionInfo[]>([])
    const [hoverTooltip, setHoverTooltip] = useState<{ visible: boolean, x: number, y: number, content: React.ReactNode } | null>(null)

    const chartContainerRef = useRef<HTMLDivElement>(null)

    const [menuOpen, setMenuOpen] = useState(false)
    const downloadMenuRef = useRef<HTMLDivElement>(null)

    const [chartCategories, setChartCategories] = useState<string[]>([])
    const [chartSeries, setChartSeries] = useState<{ name: string; data: (number | null)[] }[]>([])
    const [nLabels, setNLabels] = useState<{ yes: (number | null)[]; no: (number | null)[] }>({ yes: [], no: [] })
    const [isMobile, setIsMobile] = useState(false);

    const [columnIndices, setColumnIndices] = useState<{
        monthIdx: number
        questionNameIdx: number
        questionLabelIdx: number
        stratIdx: number
        huddleIdx: number
        numeratorIdx: number
        denominatorIdx: number
        raceIdx: number
    } | null>(null)

    useClickOutside([downloadMenuRef], () => setMenuOpen(false), menuOpen)

    const displayRaceLabel = (v: string): string => {
        const s = String(v || "").trim()
        if (!s) return ""
        if (normalizeKey(s) === normalizeKey(ALL_OTHERS_API_VALUE)) return ALL_OTHERS_LABEL
        if (normalizeKey(s) === normalizeKey("Race not documented")) return "Race Not Documented"
        if (normalizeKey(s) === normalizeKey("Others") || normalizeKey(s) === normalizeKey("Other")) return OTHER_RACE_ETHNICITY_LABEL
        return s
    }

    const isAllOthersValue = (v: string): boolean => {
        return normalizeKey(v) === normalizeKey(ALL_OTHERS_API_VALUE)
    }

    const isNHWhiteValue = (v: string): boolean => {
        const k = normalizeKey(v)
        return (
            k === normalizeKey("NH White") ||
            k === normalizeKey("Non-Hispanic White") ||
            k === normalizeKey("Non Hispanic White") ||
            k === "nhwhite" ||
            k.includes("nonhispanicwhite")
        )
    }

    const handleDownloadPNG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Ethnicity_${selectedQuestion || 'Question'}`
        const subtitle = questions.find(q => q.name === selectedQuestion)?.fullText || ''
        await exportToPNG(chartContainerRef.current, filename,
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-ethnicity-page', hideSelectors: ['.chart-subtitle'], scale: 3 }
        )
    }

    const handleDownloadSVG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Ethnicity_${selectedQuestion || 'Question'}`
        const subtitle = questions.find(q => q.name === selectedQuestion)?.fullText || ''
        await exportToSVG(chartContainerRef.current, filename,
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-ethnicity-page', hideSelectors: ['.chart-subtitle'] }
        )
    }

    const handleDownloadCSV = () => {
        const headers = ['page', 'hospital_ids', 'stratification', 'stratification_label', 'date_start', 'date_end', 'question', 'question_text', 'huddle_group', 'race_ethnicity', 'numerator', 'denominator', 'percent']
        const rows: (string | number)[][] = []

        const dateStart = globalMonthList[sliderStartIdx]?.label || ''
        const dateEnd = globalMonthList[sliderEndIdx]?.label || ''

        const dateStartFormatted = formatDateLabel(dateStart)
        const dateEndFormatted = formatDateLabel(dateEnd)

        const stratLabel = stratification === 'ExcludeNotSure' ? 'Exclude "Not"' : 'Include "Not"'

        const questionCode = selectedQuestionData?.code || ''
        const questionText = selectedQuestionData?.fullText || ''

        const hospitalIds = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join(',') : selectedHospitalIds

        selectedRaceGroups.forEach((race, idx) => {
            const yesVal = chartSeries.find(s => s.name === 'Yes Huddle')?.data[idx]
            const noVal = chartSeries.find(s => s.name === 'No Huddle')?.data[idx]
            const yesDenom = nLabels.yes[idx]
            const noDenom = nLabels.no[idx]

            const yesNum = yesDenom != null && yesVal != null ? Math.round((yesVal / 100) * yesDenom) : 0
            const noNum = noDenom != null && noVal != null ? Math.round((noVal / 100) * noDenom) : 0

            rows.push([
                'agreement_ethnicity', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                'Yes Huddle', displayRaceLabel(race), yesNum, yesDenom ?? 0,
                yesVal != null ? `${Math.round(yesVal)}%` : '0%'
            ])

            rows.push([
                'agreement_ethnicity', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                'No Huddle', displayRaceLabel(race), noNum, noDenom ?? 0,
                noVal != null ? `${Math.round(noVal)}%` : '0%'
            ])
        })

        exportToCSV({ headers, rows, filename: `Agreement_Ethnicity_${selectedQuestion || 'Question'}` })
    }

    const handleRaceToggle = (race: string, checked: boolean) => {
        if (checked) {
            if (isAllOthersValue(race)) {
                const keepNHWhite = selectedRaceGroups.some(isNHWhiteValue)
                const nhWhiteEntry = selectedRaceGroups.find(isNHWhiteValue)
                setSelectedRaceGroups(keepNHWhite && nhWhiteEntry
                    ? [ALL_OTHERS_API_VALUE, nhWhiteEntry]
                    : [ALL_OTHERS_API_VALUE])
                return
            }

            const allOthersSelected = selectedRaceGroups.some(isAllOthersValue)
            if (allOthersSelected && !isNHWhiteValue(race)) {
                return
            }

            if (selectedRaceGroups.length >= BASE_MAX_RACE_SELECTIONS) {
                return
            }
            if (!selectedRaceGroups.includes(race)) {
                setSelectedRaceGroups([...selectedRaceGroups, race])
            }
        } else {
            if (isAllOthersValue(race)) {
                setSelectedRaceGroups([...DEFAULT_RACE_SELECTIONS])
                return
            }
            setSelectedRaceGroups(selectedRaceGroups.filter(r => r !== race))
        }
    }

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setInitialLoading(false)
            setRawData(null)
            setMonthList([])
            setQuestions([])
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)
        setInitialLoading(true)

        const ethnicityFetch = fetch(`/api/tmb/agreement_race_ethnicity/${hospitalIdsParam}`)
            .then(res => {
                if (!res.ok) {
                    if (res.status === 404) {
                        throw new Error('No data available for selected hospital(s)')
                    }
                    return res.json().then(errData => {
                        throw new Error(errData.error || `API error: ${res.status}`)
                    })
                }
                return res.json()
            })

        const singleFetch = fetch(`/api/tmb/agreement_single/${hospitalIdsParam}`)
            .then(res => {
                if (!res.ok) return null
                return res.json()
            })
            .catch(() => null)

        Promise.all([ethnicityFetch, singleFetch])
            .then(([ethnicityData, singleData]) => {
                applyLaunchDateFilterToZohoData(ethnicityData)
                const rows = ethnicityData?.response?.result?.rows || []
                const columns = ethnicityData?.response?.result?.column_order || []

                if (rows.length === 0 || !columns.length) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                setRawData(ethnicityData)

                const monthIdx = resolveMonthIdx(columns, rows)
                const questionNameIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionName) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionName)
                    : findColumnIndexByRegex(columns, [/questionname/])
                const questionLabelIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel)
                    : findColumnIndexByRegex(columns, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])
                const stratIdx = findColumnIndex(columns, COLUMN_CANDIDATES.stratification) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.stratification)
                    : findColumnIndexByRegex(columns, [/strat/])
                const huddleIdx = findColumnIndex(columns, COLUMN_CANDIDATES.huddle) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.huddle)
                    : findColumnIndexByRegex(columns, [/huddlegroup/, /huddle/])
                const numeratorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.numerator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.numerator)
                    : findColumnIndexByRegex(columns, [/numerator/, /num/])
                const denominatorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.denominator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.denominator)
                    : findColumnIndexByRegex(columns, [/denominator/, /denom/])
                const raceIdx = findColumnIndex(columns, COLUMN_CANDIDATES.race) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.race)
                    : findColumnIndexByRegex(columns, [/raceethnic/, /race.*ethnic/])

                setColumnIndices({
                    monthIdx,
                    questionNameIdx,
                    questionLabelIdx,
                    stratIdx,
                    huddleIdx,
                    numeratorIdx,
                    denominatorIdx,
                    raceIdx
                })

                setActualMonths(extractActualMonthValues(rows, monthIdx))
                const months = generateMonthList(rows, monthIdx)
                setMonthList(months)

                if (months.length > 0) {
                    setSliderRange(prev => initializeSliderValues(months, prev, 12))
                }

                let labelFallbackMap: Map<string, string> | undefined
                if (singleData) {
                    const singleRows = singleData?.response?.result?.rows || []
                    const singleCols = singleData?.response?.result?.column_order || []
                    const sQnIdx = findColumnIndex(singleCols, COLUMN_CANDIDATES.questionName) !== -1
                        ? findColumnIndex(singleCols, COLUMN_CANDIDATES.questionName)
                        : findColumnIndexByRegex(singleCols, [/questionname/])
                    const sQlIdx = findColumnIndex(singleCols, COLUMN_CANDIDATES.questionLabel) !== -1
                        ? findColumnIndex(singleCols, COLUMN_CANDIDATES.questionLabel)
                        : findColumnIndexByRegex(singleCols, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])

                    labelFallbackMap = buildQuestionLabelMap(singleRows, sQnIdx, sQlIdx)
                }

                const questionList = extractQuestionsFromRows(rows, questionNameIdx, questionLabelIdx, labelFallbackMap)
                setQuestions(questionList)
                if (questionList.length > 0 && !selectedQuestion) {
                    setSelectedQuestion(questionList[0].name)
                }

                if (raceIdx !== -1) {
                    const raceSet = new Set<string>()
                    rows.forEach((r: any[]) => {
                        const raw = String(r[raceIdx] || "").trim()
                        if (!raw) return
                        let val = raw
                        if (normalizeKey(raw) === normalizeKey("Others") || normalizeKey(raw) === normalizeKey("Other")) val = OTHER_RACE_ETHNICITY_LABEL
                        else if (normalizeKey(raw) === normalizeKey("Race not documented")) val = "Race Not Documented"
                        else if (isNHWhiteValue(raw)) val = "NH White"
                        raceSet.add(val)
                    })

                    const preferredOrder = [
                        ALL_OTHERS_API_VALUE,
                        "Hispanic",
                        "Black/African American",
                        "Asian",
                        "Race Not Documented",
                        OTHER_RACE_ETHNICITY_LABEL,
                        "NH White"
                    ]

                    const foundRaces = Array.from(raceSet)
                    const ordered = preferredOrder.filter(r => foundRaces.some(f => normalizeKey(f) === normalizeKey(r)))
                    const remaining = foundRaces.filter(r => !preferredOrder.some(p => normalizeKey(p) === normalizeKey(r)))
                    const merged = [...ordered, ...remaining.sort()]

                    setAvailableRaceGroups(merged)

                    const canonicalized = selectedRaceGroups.map(r => {
                        if (normalizeKey(r) === normalizeKey("Others") || normalizeKey(r) === normalizeKey("Other")) return OTHER_RACE_ETHNICITY_LABEL
                        if (normalizeKey(r) === normalizeKey("Race not documented")) return "Race Not Documented"
                        if (isNHWhiteValue(r)) return "NH White"
                        return r
                    })

                    const validSelected = canonicalized.filter(r =>
                        merged.some(m => normalizeKey(m) === normalizeKey(r))
                    )
                    if (validSelected.length === 0) {
                        setSelectedRaceGroups(merged.slice(0, BASE_MAX_RACE_SELECTIONS))
                    } else if (JSON.stringify(canonicalized) !== JSON.stringify(selectedRaceGroups) || validSelected.length !== canonicalized.length) {
                        setSelectedRaceGroups(validSelected.slice(0, BASE_MAX_RACE_SELECTIONS))
                    }
                } else {
                    setAvailableRaceGroups([])
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData || !columnIndices) {
            setChartCategories([])
            setChartSeries([])
            setNLabels({ yes: [], no: [] })
            return
        }

        const rows = rawData?.response?.result?.rows || []
        if (!rows.length) return

        const { monthIdx, questionNameIdx, stratIdx, huddleIdx, numeratorIdx, denominatorIdx, raceIdx } = columnIndices

        let filteredRows = rows

        if (monthIdx !== -1 && monthList.length > 0) {
            const minMonthVal = sliderRange[0] || 0
            const maxMonthVal = sliderRange[1] || 0
            if (minMonthVal > 0 || maxMonthVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minMonthVal && v <= maxMonthVal
                })
            }
        }

        if (selectedQuestion && questionNameIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return String(r[questionNameIdx] || "").trim() === selectedQuestion
            })
        }

        if (stratIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return normalizeKey(r[stratIdx]) === normalizeKey(stratification)
            })
        }

        if (filteredRows.length === 0) {
            setChartCategories([])
            setChartSeries([])
            setNLabels({ yes: [], no: [] })
            return
        }

        const selectedRaceByKey: Record<string, string> = {}
        selectedRaceGroups.forEach(v => {
            selectedRaceByKey[normalizeKey(v)] = v
        })

        const dataByRace: Record<string, HuddleTotals> = {}
        selectedRaceGroups.forEach(race => {
            dataByRace[race] = {
                'Yes Huddle': { num: 0, denom: 0, hasNum: false, hasDenom: false },
                'No Huddle': { num: 0, denom: 0, hasNum: false, hasDenom: false }
            }
        })

        filteredRows.forEach((r: any[]) => {
            if (raceIdx === -1 || huddleIdx === -1) return

            const rawRace = String(r[raceIdx] || "").trim()
            if (!rawRace) return

            let canonicalRaw = rawRace
            if (normalizeKey(rawRace) === normalizeKey("Others") || normalizeKey(rawRace) === normalizeKey("Other")) canonicalRaw = OTHER_RACE_ETHNICITY_LABEL
            else if (normalizeKey(rawRace) === normalizeKey("Race not documented")) canonicalRaw = "Race Not Documented"
            else if (isNHWhiteValue(rawRace)) canonicalRaw = "NH White"

            const raceValue = selectedRaceByKey[normalizeKey(canonicalRaw)]
            if (!raceValue) return

            const huddleGroup = normalizeHuddleGroup(r[huddleIdx])
            if (huddleGroup !== 'Yes Huddle' && huddleGroup !== 'No Huddle') return

            const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
            const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null

            if (num !== null) {
                dataByRace[raceValue][huddleGroup].num += num
                dataByRace[raceValue][huddleGroup].hasNum = true
            }
            if (denom !== null) {
                dataByRace[raceValue][huddleGroup].denom += denom
                dataByRace[raceValue][huddleGroup].hasDenom = true
            }
        })

        const categories: string[] = []
        const yesHuddleData: (number | null)[] = []
        const noHuddleData: (number | null)[] = []
        const yesDenoms: (number | null)[] = []
        const noDenoms: (number | null)[] = []

        const orderedRaceGroups = [...selectedRaceGroups].sort((a, b) => {
            const aIsNH = isNHWhiteValue(a) ? 0 : 1
            const bIsNH = isNHWhiteValue(b) ? 0 : 1
            return aIsNH - bIsNH
        })

        orderedRaceGroups.forEach(race => {
            const data = dataByRace[race]
            categories.push(displayRaceLabel(race))

            const yesPct = data['Yes Huddle'].denom > 0 && data['Yes Huddle'].hasNum && data['Yes Huddle'].hasDenom
                ? (data['Yes Huddle'].num / data['Yes Huddle'].denom) * 100
                : null
            const noPct = data['No Huddle'].denom > 0 && data['No Huddle'].hasNum && data['No Huddle'].hasDenom
                ? (data['No Huddle'].num / data['No Huddle'].denom) * 100
                : null

            yesHuddleData.push(yesPct)
            noHuddleData.push(noPct)
            yesDenoms.push(data['Yes Huddle'].hasDenom ? data['Yes Huddle'].denom : null)
            noDenoms.push(data['No Huddle'].hasDenom ? data['No Huddle'].denom : null)
        })

        setChartCategories(categories)
        setChartSeries([
            { name: 'No Huddle', data: noHuddleData },
            { name: 'Yes Huddle', data: yesHuddleData }
        ])
        setNLabels({ yes: yesDenoms, no: noDenoms })

    }, [rawData, columnIndices, monthList, sliderRange, selectedQuestion, stratification, selectedRaceGroups])
    useEffect(() => {
        const checkScreenSize = () => {
        setIsMobile(window.innerWidth <= 768); 
        };

        checkScreenSize();

        window.addEventListener("resize", checkScreenSize);

        return () => {
        window.removeEventListener("resize", checkScreenSize);
        };
    }, []);
    const selectedQuestionData = questions.find(q => q.name === selectedQuestion)
    const maxBarHeight = 320

    const numRaces = selectedRaceGroups.length
    const barGroupMaxWidth = numRaces === 1 ? 85 : numRaces === 2 ? 68 : numRaces <= 4 ? 50 : 38

    const isMaxRaceSelected = selectedRaceGroups.length >= BASE_MAX_RACE_SELECTIONS
    const allOthersActive = selectedRaceGroups.some(isAllOthersValue)

    if (initialLoading) {
        return <LoadingNotice title="Loading agreement data..." />
    }

    if (error) {
        return (
            <div className="agreement-ethnicity-page tmb-page">
                <div className="content-wrapper">
                    <div className="header-wrapper">
                        <span className="page-title title--fixed">
                            <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                            <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                        </span>
                    </div>
                    <div className="section-card" style={{ minHeight: 'auto' }}>
                        {error.includes('No data available') ? (
                            <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                                No data available
                            </div>
                        ) : (
                            <p>Error: {error}</p>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id={generatePDFIds.dashboard()} className="agreement-ethnicity-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper header--stratified">
                    <span className="page-title title--fixed">
                        <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                        <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                    </span>

                    <div className="controls-col controls--row">
                        <TmbControlsRow>
                            {globalMonthList.length > 0 && (
                                <DateRangeSlider
                                    monthList={globalMonthList}
                                    startIndex={sliderStartIdx}
                                    endIndex={sliderEndIdx}
                                    onChange={(start, end) => setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])}
                                />
                            )}

                            <TmbPillSelect
                                displayText={selectedRaceGroups.length === 0 ? "Race/Ethnicity" : (selectedRaceGroups.length === 1 ? displayRaceLabel(selectedRaceGroups[0]) : `${selectedRaceGroups.length} selected`)}
                                variant="multiselect"
                                isOpen={raceDropdownOpen}
                                onToggle={setRaceDropdownOpen}
                            >
                                    {(() => {
                                        const allOthersItem = availableRaceGroups.find(r => isAllOthersValue(r))
                                        const nhWhiteItem = availableRaceGroups.find(r => isNHWhiteValue(r))
                                        const subItems = availableRaceGroups.filter(r => !isAllOthersValue(r) && !isNHWhiteValue(r))

                                        const renderRaceLabel = (race: string) => {
                                            const isChecked = selectedRaceGroups.includes(race)
                                            const isThisAllOthers = isAllOthersValue(race)
                                            const isThisNHWhite = isNHWhiteValue(race)
                                            let isDisabled = false
                                            if (allOthersActive && !isThisAllOthers && !isThisNHWhite) {
                                                isDisabled = true
                                            } else if (!isChecked && isMaxRaceSelected && !isThisAllOthers) {
                                                isDisabled = true
                                            }
                                            return (
                                                <label
                                                    key={race}
                                                    className={`race-item-label ${isDisabled ? 'disabled' : ''} ${allOthersActive && !isThisAllOthers && !isThisNHWhite ? 'locked-checked' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className={`race-checkbox${allOthersActive && !isThisAllOthers && !isThisNHWhite ? ' locked-checked' : ''}`}
                                                        checked={allOthersActive && !isThisAllOthers && !isThisNHWhite ? true : isChecked}
                                                        onChange={(e) => handleRaceToggle(race, e.target.checked)}
                                                        disabled={isDisabled}
                                                    />
                                                    {displayRaceLabel(race)}
                                                    {isThisAllOthers && (
                                                        <span className="race-info-icon" title="Select to view an aggregated comparison of all other race/ethnicity groups.">i</span>
                                                    )}
                                                </label>
                                            )
                                        }

                                        return (
                                            <>
                                                {allOthersItem && renderRaceLabel(allOthersItem)}
                                                {subItems.length > 0 && (
                                                    <div className="race-sub-options">
                                                        {subItems.map(race => renderRaceLabel(race))}
                                                    </div>
                                                )}
                                                {nhWhiteItem && renderRaceLabel(nhWhiteItem)}
                                            </>
                                        )
                                    })()}
                            </TmbPillSelect>

                            <TmbStratificationPill value={stratification} onChange={setStratification} />

                            <TmbQuestionPill questions={questions} selectedQuestion={selectedQuestion} onSelect={setSelectedQuestion} />

                        </TmbControlsRow>
                    </div>
                </div>

                <div className="section-card" ref={chartContainerRef} style={!hasDataForRange ? { minHeight: 'auto' } : undefined}>
                    {error ? (
                        <div className="error-container">{error}</div>
                    ) : loading ? (
                        <LoadingNotice />
                    ) : !chartSeries.length || !chartCategories.length || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                            No data available
                        </div>
                    ) : (<>
                    <div className="custom-download-menu" ref={downloadMenuRef}>
                        <div className="hamburger-icon" onClick={() => setMenuOpen(!menuOpen)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <g>
                                    <rect x="4" y="6" width="16" height="2" rx="1" />
                                    <rect x="4" y="11" width="16" height="2" rx="1" />
                                    <rect x="4" y="16" width="16" height="2" rx="1" />
                                </g>
                            </svg>
                        </div>
                        {menuOpen && (
                            <div className="download-dropdown show">
                                <div className="menu-items-row">
                                    <div className="menu-item" onClick={async () => { await handleDownloadPNG(); setMenuOpen(false); }}>Download PNG</div>
                                    <div className="menu-item" onClick={async () => { await handleDownloadSVG(); setMenuOpen(false); }}>Download SVG</div>
                                    <div className="menu-item" onClick={() => { handleDownloadCSV(); setMenuOpen(false); }}>Download CSV</div>
                                </div>
                            </div>
                        )}
                    </div>
                    {selectedQuestionData && (
                        <div className="chart-subtitle">
                            {selectedQuestionData.fullText}
                        </div>
                    )}
                        <div className="chart-with-overlay">
                            <div className="ethnicity-chart-flex">
                                <div className="ethnicity-chart-main">

                                    <div className="ethnicity-column-headers">
                                        {chartCategories.map((race, idx) => (
                                            <div key={idx} className="column-header-item">
                                                {race}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="ethnicity-table-wrap">
                                        <table className="ethnicity-bar-table">
                                            <tbody>
                                                <tr>
                                                    {chartCategories.map((_, idx) => {
                                                        const yesSeriesObj = chartSeries.find(s => s.name === "Yes Huddle");
                                                        const noSeriesObj = chartSeries.find(s => s.name === "No Huddle");

                                                        const yesVal = yesSeriesObj?.data ? yesSeriesObj.data[idx] : null;
                                                        const noVal = noSeriesObj?.data ? noSeriesObj.data[idx] : null;

                                                        const yesHeight = yesVal != null ? (yesVal / 100) * maxBarHeight : 0;
                                                        const noHeight = noVal != null ? (noVal / 100) * maxBarHeight : 0;

                                                        const yesLabel = yesVal != null ? Math.round(yesVal) + '%' : '0%';
                                                        const noLabel = noVal != null ? Math.round(noVal) + '%' : '0%';

                                                        return (
                                                            <td key={idx}>
                                                                <div className="bar-cell">
                                                                    <div
                                                                        className="bar-group"
                                                                        onMouseEnter={(e) => {
                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            setHoverTooltip({
                                                                                visible: true,
                                                                               x: !isMobile? rect.right + 10: rect.right/2,
                                                                                y: rect.top + (rect.height / 2) - 30,
                                                                                content: (
                                                                                    <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                        <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                            No Huddle {nLabels.no[idx] ?? 0}
                                                                                        </div>
                                                                                        <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                            <span style={{ backgroundColor: '#D79852', width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                            <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Agree:</span>
                                                                                            <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                                {noLabel}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            });
                                                                        }}
                                                                        onMouseLeave={() => setHoverTooltip(null)}
                                                                    >
                                                                        {(noVal !== null && noVal > 0) && <div className="bar-value">{noLabel}</div>}
                                                                        {(noVal === 0 || noVal === null) && <div className="bar-value">0%</div>}
                                                                        <div
                                                                            className="bar-fill no-huddle"
                                                                            style={{ height: `${noHeight}px` }}
                                                                        ></div>
                                                                    </div>

                                                                    <div
                                                                        className="bar-group"
                                                                        onMouseEnter={(e) => {
                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            setHoverTooltip({
                                                                                visible: true,
                                                                                 x: !isMobile? rect.right + 10: rect.right/2,
                                                                                y: rect.top + (rect.height / 2) - 30,
                                                                                content: (
                                                                                    <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                        <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                            Yes Huddle {nLabels.yes[idx] ?? 0}
                                                                                        </div>
                                                                                        <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                            <span style={{ backgroundColor: '#3F548D', width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                            <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Agree:</span>
                                                                                            <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                                {yesLabel}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            });
                                                                        }}
                                                                        onMouseLeave={() => setHoverTooltip(null)}
                                                                    >
                                                                        {(yesVal !== null && yesVal > 0) && <div className="bar-value">{yesLabel}</div>}
                                                                        {(yesVal === 0 || yesVal === null) && <div className="bar-value">0%</div>}
                                                                        <div
                                                                            className="bar-fill yes-huddle"
                                                                            style={{ height: `${yesHeight}px` }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="ethnicity-n-values-row">
                                        <span className="n-label">N=</span>
                                        {chartCategories.map((_, idx) => {
                                            const yesNVal = nLabels.yes[idx];
                                            const noNVal = nLabels.no[idx];

                                            return (
                                                <div key={idx} className="n-value-column">
                                                    <span className="n-value no" style={{ maxWidth: `${barGroupMaxWidth}px` }}>{noNVal ?? 0}</span>
                                                    <span className="n-value yes" style={{ maxWidth: `${barGroupMaxWidth}px` }}>{yesNVal ?? 0}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                                <div className="ethnicity-legend-col">
                                    <div className="ethnicity-legend">
                                        <div className="legend-item">
                                            <div className="legend-dot no-huddle"></div>
                                            <span>No Huddle</span>
                                        </div>
                                        <div className="legend-item">
                                            <div className="legend-dot yes-huddle"></div>
                                            <span>Yes Huddle</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>)}
                </div>
            </div>
            {hoverTooltip && hoverTooltip.visible && (
                <div style={{
                    position: 'fixed',
                    top: hoverTooltip.y,
                    left: hoverTooltip.x,
                    zIndex: 10000,
                    pointerEvents: 'none',
                    transform: 'translateY(-50%)'
                }}>
                    {hoverTooltip.content}
                </div>
            )}

        </div >
    )
}

export default AgreementEthnicity
